/** @jsxImportSource @emotion/react */
import React from "react";
import {css} from "@emotion/react";
import {Switch} from "@mui/material";

import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import Tooltip from "js/common/views/tooltips";
import * as SavedConfigs from "js/common/saved-configs";
import * as Popups from "js/common/popups";
import * as Auditor from "js/common/auditor";
import * as Rata from "js/common/utils/remote-data";
import * as Users from "js/common/users";
import * as DashboardUtils from "js/dashboards/utils";
import * as SharingUtils from "js/common/views/sharing/utils";
import SaveReportAsDialog from "js/common/views/save-as-dialog";

export const COLLAPSED_WIDTH = 50;
export const EXPANDED_WIDTH = 170;
const EDIT_INDICATOR_WIDTH = 4;


const sidebarWrapperStyle = (theme) => css`
  position: relative;
  flex-shrink: 0;
  background: ${theme.palette.background.card};
  border-right: 1px solid ${theme.palette.border ? theme.palette.border.light : "rgba(0,0,0,0.12)"};
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const toggleRowStyle = css`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 8px 8px 4px 8px;
`;

const toggleButtonStyle = (theme) => css`
  cursor: pointer;
  width: 26px;
  height: 26px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.palette.text.main};
  opacity: 0.45;
  transition: opacity 0.15s, background 0.15s;
  font-size: 11px;

  &:hover {
    opacity: 1;
    background: ${theme.themeId === "light" ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.1)"};
  }
`;

const itemRowStyle = (theme, disabled, isExpanded) => css`
  display: flex;
  align-items: center;
  padding: ${isExpanded ? "9px 12px 9px 14px" : "9px 0"};
  justify-content: ${isExpanded ? "flex-start" : "center"};
  cursor: ${disabled ? "default" : "pointer"};
  opacity: ${disabled ? 0.38 : 1};
  transition: background 0.15s;
  position: relative;

  &:hover {
    background: ${!disabled ? (theme.themeId === "light" ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.07)") : "none"};
  }
`;

const iconWrapStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 22px;
  flex-shrink: 0;
  font-size: 16px;
`;

const stackedBadgeStyle = (theme) => css`
  position: absolute;
  top: -3px;
  right: -5px;
  font-size: 9px;
  background: ${theme.palette.background.paper};
  border-radius: 50%;
  padding: 1px 2px;
  line-height: 1;
  color: ${theme.palette.text.main};
`;

const labelStyle = (theme, isExpanded) => css`
  margin-left: ${isExpanded ? "10px" : "0"};
  font-size: 13px;
  font-family: ${theme.typography.fontFamily};
  color: ${theme.palette.text.main};
  white-space: nowrap;
  overflow: hidden;
  max-width: ${isExpanded ? "200px" : "0"};
  opacity: ${isExpanded ? 1 : 0};
`;

const unsavedDotStyle = (theme) => css`
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: ${theme.palette.error.main};
  position: absolute;
  top: -2px;
  right: -3px;
  pointer-events: none;
`;

const editToggleRowStyle = (theme, isExpanded, isEditing) => css`
  display: flex;
  align-items: center;
  padding: ${isExpanded ? "9px 8px 9px 10px" : "9px 0"};
  justify-content: ${isExpanded ? "space-between" : "center"};
  cursor: pointer;
  transition: background 0.15s;
  border-left: ${EDIT_INDICATOR_WIDTH}px solid ${isEditing ? theme.palette.primary.main : "transparent"};

  &:hover {
    background: ${theme.themeId === "light" ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.07)"};
  }
`;


const SidebarItem = React.memo(({icon, stackedIcon, label, tooltip, isExpanded, onClick, disabled, testId, showDot, width, wrap}) => {
  const {theme} = React.useContext(CustomThemeContext);

  const inner = (
      <div
          role="button"
          data-test-id={testId}
          aria-disabled={disabled || undefined}
          css={itemRowStyle(theme, disabled, isExpanded)}
          onClick={disabled ? undefined : onClick}
      >
        <div css={iconWrapStyle}>
          <i className={`bhi-${icon}`} />
          {stackedIcon && (
              <i className={`bhi-${stackedIcon}`} css={stackedBadgeStyle(theme)} />
          )}
          {showDot && <span data-test-id="unsaved-changes-indicator" css={unsavedDotStyle(theme)} />}
        </div>
        <span css={labelStyle(theme, isExpanded)}>{label}</span>
      </div>
  );

  // Always show tooltip on right; in expanded mode still show it for keyboard/accessibility clarity
  return (
      <Tooltip
          text={tooltip}
          position="right"
          width={width}
          wrap={wrap}
          disable={!tooltip || (isExpanded && !disabled)}
      >
        {inner}
      </Tooltip>
  );
});

const EditToggleItem = React.memo(({isExpanded, isEditing, setEditing, theme}) => {
  const handleToggle = () => {
    const next = !isEditing;
    next ? document.body.classList.add("isEditing") : document.body.classList.remove("isEditing");
    setEditing(next);
  };

  const inner = (
      <div
          data-test-id="toggle-edit-mode"
          css={editToggleRowStyle(theme, isExpanded, isEditing)}
          onClick={handleToggle}
      >
        <div style={{display: "flex", alignItems: "center"}}>
          <div css={iconWrapStyle}>
            <i
                className="bhi-edit"
                style={{color: isEditing ? theme.palette.primary.main : undefined}}
            />
          </div>
          <span css={labelStyle(theme, isExpanded)}>Edit</span>
        </div>
        {isExpanded && (
            <Switch
                data-product-id="edit-mode-switch"
                size="small"
                checked={isEditing}
                onChange={e => {
                  e.stopPropagation();
                  const next = e.target.checked;
                  next ? document.body.classList.add("isEditing") : document.body.classList.remove("isEditing");
                  setEditing(next);
                }}
                onClick={e => e.stopPropagation()}
            />
        )}
      </div>
  );

  return (
      <Tooltip
          text={isEditing ? "Turn off edit mode" : "Turn on edit mode"}
          position="right"
          disable={isExpanded}
      >
        {inner}
      </Tooltip>
  );
});


const DashboardSidebar = React.memo(({
  isExpanded,
  onToggleExpanded,
  dashboard,
  onDashboardChange,
  setSavingDashboard,
  handleDashboardSave,
  handleDashboardSaveAs,
  ownershipTypeToDashboardsWrapper,
  setOwnershipTypeToDashboardsWrapper,
  isEditing,
  setEditing,
  hasUnsavedChanges,
  hasOutOfBoundsPanels = false,
  isCreatingDashboard = false,
  onRequestNewDashboard,
  onRequestOpenSavedDashboards,
}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const {isFullEditor, isBasicEditor} = DashboardUtils.useEditorFlags();
  const [showSaveAsDialog, setShowSaveAsDialog] = React.useState(false);

  // Permission checks
  const userHasPermission = React.useMemo(() => {
    if (!isFullEditor) return false;
    if (dashboard?.has("sharedWith")) {
      const permission = SharingUtils.getCurrentUserPermissionForConfig(dashboard);
      return permission === "EDIT";
    }
    return false;
  }, [dashboard, isFullEditor]);

  const dashboardOwnerId = dashboard?.get("ownerId");
  const isDashboardOwner = dashboardOwnerId === Users.getCurrentUser().get("id");
  const isDashboardOwnerOrEditor = isDashboardOwner || userHasPermission;
  const isSystemDefault = dashboard?.get("systemDefault");
  const isSaveRestricted = isSystemDefault || !isDashboardOwnerOrEditor;

  const handleSaveRequest = React.useCallback(() => {
    const id = dashboard.get("id");
    const name = dashboard.get("name");
    setSavingDashboard(true);
    SavedConfigs
        .update(dashboard)
        .then(
            savedDashboard => {
              Popups.namedItemUpdated(name);
              Auditor.audit("dashboards:save", {id, name});
              handleDashboardSave(savedDashboard);
              onDashboardChange(savedDashboard);
              setOwnershipTypeToDashboardsWrapper(wrapper => Rata.updateValue(wrapper, ownershipTypeToDashboards => {
                const ownershipType = ownershipTypeToDashboards.findKey(
                    configs => configs.some(config => config.get("id") === id));
                return ownershipType
                    ? ownershipTypeToDashboards.update(ownershipType, configs => configs
                        .map(config => config.get("id") === id ? savedDashboard : config))
                    : ownershipTypeToDashboards;
              }));
            },
            err => {
              const errorMsg = "Unable to save dashboard";
              if (err.responseJSON?.message) {
                Popups.error(`${errorMsg}: ${err.responseJSON.message}`);
              } else {
                Popups.error(errorMsg);
              }
            })
        .finally(() => setSavingDashboard(false));
  }, [dashboard, onDashboardChange, handleDashboardSave, setOwnershipTypeToDashboardsWrapper, setSavingDashboard]);

  const handleSaveAsRequest = React.useCallback(dashboardName => {
    setShowSaveAsDialog(false);
    setSavingDashboard(true);
    SavedConfigs
        .create(dashboard.set("name", dashboardName).delete("systemDefault"))
        .then(
            savedDashboard => {
              Popups.namedItemSaved(dashboardName);
              Auditor.audit("dashboards:save-as", {dashboardName});
              handleDashboardSaveAs(savedDashboard);
              onDashboardChange(savedDashboard);
              setOwnershipTypeToDashboardsWrapper(wrapper => Rata.updateValue(wrapper, x => x
                  .update("ownedConfigs", ownedConfigs => ownedConfigs.push(savedDashboard))));
            },
            err => {
              const errorMsg = "Unable to save dashboard";
              if (err.responseJSON?.message) {
                Popups.error(`${errorMsg}: ${err.responseJSON.message}`);
              } else {
                Popups.error(errorMsg);
              }
            })
        .finally(() => setSavingDashboard(false));
  }, [dashboard, handleDashboardSaveAs, onDashboardChange, setOwnershipTypeToDashboardsWrapper, setSavingDashboard]);

  const handleSaveClick = dashboard?.get("id") ? handleSaveRequest : () => setShowSaveAsDialog(true);

  const saveTooltip = isSaveRestricted
      ? "This dashboard is owned by another user or is generated by Bullhorn and cannot be edited. Use Save As instead."
      : hasOutOfBoundsPanels
          ? "Move all panels inside the dashboard boundary before saving"
          : "Save";

  return (
      <>
        <div
            css={sidebarWrapperStyle(theme)}
            className="hide-for-print"
            style={{width: isExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH}}>

          {/* Collapse / expand toggle */}
          <div css={toggleRowStyle}>
            <Tooltip
                text={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
                position="right">
              <div css={toggleButtonStyle(theme)} onClick={onToggleExpanded}>
                <i className={`fa fa-chevron-${isExpanded ? "left" : "right"}`} />
              </div>
            </Tooltip>
          </div>

          {isBasicEditor && !isCreatingDashboard && !!dashboard && (
              <EditToggleItem
                  isExpanded={isExpanded}
                  isEditing={isEditing}
                  setEditing={setEditing}
                  theme={theme}
              />
          )}

          <div style={{position: "relative"}}>
            <SidebarItem
                icon="save"
                label="Save"
                tooltip={saveTooltip}
                width={isSaveRestricted || hasOutOfBoundsPanels ? 300 : undefined}
                wrap={isSaveRestricted || hasOutOfBoundsPanels}
                isExpanded={isExpanded}
                onClick={handleSaveClick}
                disabled={isCreatingDashboard || isSaveRestricted || hasOutOfBoundsPanels}
                testId="save-dashboard-button"
                showDot={!isSaveRestricted && !hasOutOfBoundsPanels && hasUnsavedChanges}
            />
          </div>

          <div style={{position: "relative"}}>
            <SidebarItem
                icon="save"
                stackedIcon="edit"
                label="Save As"
                tooltip={hasOutOfBoundsPanels ? "Move all panels inside the dashboard boundary before saving" : "Save As"}
                width={hasOutOfBoundsPanels ? 300 : undefined}
                wrap={hasOutOfBoundsPanels}
                isExpanded={isExpanded}
                onClick={() => setShowSaveAsDialog(true)}
                disabled={!dashboard || isCreatingDashboard || hasOutOfBoundsPanels}
                testId="save-as-dashboard-button"
                showDot={!hasOutOfBoundsPanels && hasUnsavedChanges && isSaveRestricted}
            />
          </div>

          {isFullEditor && (
              <SidebarItem
                  icon="plus"
                  label="New"
                  tooltip="New Dashboard"
                  isExpanded={isExpanded}
                  onClick={onRequestNewDashboard}
                  disabled={isCreatingDashboard}
                  testId="new-dashboard-button"
              />
          )}

          <SidebarItem
              icon="file"
              label="Open"
              tooltip="Open Dashboard"
              isExpanded={isExpanded}
              onClick={onRequestOpenSavedDashboards}
              testId="open-dashboard-button"
          />

        </div>

        {showSaveAsDialog && (
            <SaveReportAsDialog
                initialName={dashboard.get("name")}
                savingTypeName="dashboard"
                generateUniqueName={false}
                onSaveAsRequest={handleSaveAsRequest}
                onCancelRequest={() => setShowSaveAsDialog(false)}
                unavailableNames={Rata
                    .getValue(ownershipTypeToDashboardsWrapper)
                    .get("ownedConfigs")
                    .map(config => config.get("name"))} />
        )}
      </>
  );
});

export default DashboardSidebar;
